import React, { useState, useEffect, useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import Slider from "@mui/material/Slider";
import {
  Container,
  SliderTrack,
  SliderSegment,
  SliderThumb,
  SliderLabels,
  SliderValues,
  ValueItem,
} from "./styles";
import { FormatCurrency, roundCurrency } from "../../lib";

export default function SliderCheckout({
  totalPoints = 0,
  totalMoney = 0,
  userBalance = 0,
  minPoints = 0,
  onChange,
  disabled = false,
  factor = 1,
}) {
  const [sliderValue, setSliderValue] = useState(0);
  const [hasInitialized, setHasInitialized] = useState(false);

  const calculations = useMemo(() => {
    const safeTotal = Number(totalMoney) || 0;
    const safeBalance = Number(userBalance) || 0;
    const safeTotalPoints = Number(totalPoints) || 0;
    const safeMinPoints = Number(minPoints) || 0;
    const safeFactor = Number(factor) || 1;

    const maxPointsFromBalance = Math.min(safeBalance, safeTotalPoints);

    // Calcula minMoneyRequired usando a diferença de pontos primeiro para evitar erro de arredondamento
    // Em vez de: minMoneyRequired = safeTotal - (maxPointsFromBalance / safeFactor)
    // Usamos: minMoneyRequired = (safeTotalPoints - maxPointsFromBalance) / safeFactor
    const pointsDeficit = Math.max(0, safeTotalPoints - maxPointsFromBalance);
    const minMoneyRequired = safeFactor > 0 ? pointsDeficit / safeFactor : safeTotal;

    // Mantém balanceInMoney para compatibilidade com código existente
    const balanceInMoney =
      safeFactor > 0 ? maxPointsFromBalance / safeFactor : 0;
    let maxMoneyAllowed = safeTotal;

    if (safeMinPoints > 0 && safeTotalPoints > 0) {
      const minPointsInMoney = safeFactor > 0 ? safeMinPoints / safeFactor : 0;
      maxMoneyAllowed = Math.max(0, safeTotal - minPointsInMoney);
    }

    let scenario = 1;
    if (minMoneyRequired > 0) {
      scenario = 3;
    } else if (balanceInMoney >= safeTotal) {
      scenario = 1;
    } else {
      scenario = 2;
    }

    return {
      safeTotal,
      minMoneyRequired,
      maxMoneyAllowed,
      maxPointsFromBalance,
      balanceInMoney,
      scenario,
      safeTotalPoints,
      safeFactor,
      safeMinPoints,
    };
  }, [totalMoney, userBalance, totalPoints, minPoints, factor]);

  useEffect(() => {
    if (calculations.safeTotal > 0) {
      const initialMoneyValue = calculations.minMoneyRequired;
      const initialSliderValue =
        calculations.safeTotal > 0
          ? (initialMoneyValue / calculations.safeTotal) * 100
          : 0;
      setSliderValue(Math.max(0, Math.min(100, initialSliderValue)));
    }
  }, [calculations.safeTotal, calculations.minMoneyRequired]);

  const currentValues = useMemo(() => {
    if (calculations.safeTotal <= 0) {
      return {
        moneyToPay: 0,
        pointsToUse: 0,
        moneyPercentage: 0,
        pointsPercentage: 100,
      };
    }

    // Manter valores com precisão total durante cálculos
    // Arredondar apenas no final para evitar erro acumulado
    let moneyToPay = (sliderValue / 100) * calculations.safeTotal;

    // Threshold para tratar valores residuais muito pequenos (< R$ 0,05) como zero
    // Isso resolve o problema de centavos residuais devido a arredondamentos
    const MONEY_THRESHOLD = 0.05;

    if (moneyToPay < MONEY_THRESHOLD && moneyToPay > 0) {
      moneyToPay = 0;
    }

    // Calcula pontos corretamente: subtrai o equivalente em pontos do dinheiro que será pago
    // Em vez de: pointsToUse = (safeTotal - moneyToPay) * safeFactor (que introduz erro)
    // Usamos: pointsToUse = safeTotalPoints - round(moneyToPay * safeFactor)
    const isFullPoints = sliderValue === 0 || moneyToPay === 0;
    const pointsToUse = isFullPoints
      ? calculations.safeTotalPoints
      : calculations.safeTotalPoints -
        Math.round(moneyToPay * calculations.safeFactor);

    // Calcular porcentagens garantindo que sempre somem exatamente 100%
    // Evita esticamento visual devido a erros de arredondamento
    const moneyPercentage =
      calculations.safeTotal > 0
        ? (moneyToPay / calculations.safeTotal) * 100
        : 0;
    const pointsPercentage = 100 - moneyPercentage;

    return {
      moneyToPay,
      pointsToUse,
      moneyPercentage: Math.max(0, Math.min(100, moneyPercentage)),
      pointsPercentage: Math.max(0, Math.min(100, pointsPercentage)),
    };
  }, [sliderValue, calculations]);

  const sliderLimits = useMemo(() => {
    const minSlider =
      calculations.safeTotal > 0
        ? (calculations.minMoneyRequired / calculations.safeTotal) * 100
        : 0;
    const maxSlider =
      calculations.safeTotal > 0
        ? (calculations.maxMoneyAllowed / calculations.safeTotal) * 100
        : 100;
    return { minSlider, maxSlider };
  }, [calculations]);

  const handleChange = useCallback(
    (event, newValue) => {
      if (disabled) return;
      const invertedValue = 100 - newValue;
      const clampedValue = Math.max(
        sliderLimits.minSlider,
        Math.min(sliderLimits.maxSlider, invertedValue),
      );
      setSliderValue(clampedValue);
    },
    [disabled, sliderLimits],
  );

  const handleChangeCommitted = useCallback(
    (event, newValue) => {
      if (disabled) return;
      const invertedValue = 100 - newValue;
      const clampedValue = Math.max(
        sliderLimits.minSlider,
        Math.min(sliderLimits.maxSlider, invertedValue),
      );
      setSliderValue(clampedValue);

      if (onChange) {
        // Manter valores com precisão total durante cálculos
        let moneyValue = (clampedValue / 100) * calculations.safeTotal;

        // Threshold para tratar valores residuais muito pequenos (< R$ 0,05) como zero
        const MONEY_THRESHOLD = 0.05;

        if (moneyValue < MONEY_THRESHOLD && moneyValue > 0) {
          moneyValue = 0;
        }

        // Calcula pontos corretamente: subtrai o equivalente em pontos do dinheiro que será pago
        const isFullPoints = clampedValue === 0 || moneyValue === 0;
        const pointsValue = isFullPoints
          ? calculations.safeTotalPoints
          : calculations.safeTotalPoints -
            Math.round(moneyValue * calculations.safeFactor);

        onChange({
          sliderValue: clampedValue,
          moneyValue,
          pointsValue,
          moneyToPay: moneyValue,
        });
      }
    },
    [disabled, sliderLimits, calculations, onChange],
  );

  useEffect(() => {
    if (onChange && !hasInitialized && calculations.safeTotal > 0) {
      const initialMoneyValue = calculations.minMoneyRequired;
      const initialSliderValue =
        calculations.safeTotal > 0
          ? (initialMoneyValue / calculations.safeTotal) * 100
          : 0;

      const clampedValue = Math.max(0, Math.min(100, initialSliderValue));
      // Manter valores com precisão total durante cálculos
      let moneyValue = (clampedValue / 100) * calculations.safeTotal;

      // Threshold para tratar valores residuais muito pequenos (< R$ 0,05) como zero
      const MONEY_THRESHOLD = 0.05;

      if (moneyValue < MONEY_THRESHOLD && moneyValue > 0) {
        moneyValue = 0;
      }

      // Calcula pontos corretamente: subtrai o equivalente em pontos do dinheiro que será pago
      const isFullPoints = clampedValue === 0 || moneyValue === 0;
      const pointsValue = isFullPoints
        ? calculations.safeTotalPoints
        : calculations.safeTotalPoints -
          Math.round(moneyValue * calculations.safeFactor);

      onChange({
        sliderValue: clampedValue,
        moneyValue,
        pointsValue,
        moneyToPay: moneyValue,
      });

      setHasInitialized(true);
    }
  }, [onChange, calculations, hasInitialized]);

  const formatPoints = (pts) => Math.round(pts).toLocaleString("pt-BR");

  const showMoneySegment = currentValues.moneyToPay > 0.01;
  const hasPoints = userBalance > 0;
  const thumbPosition = currentValues.pointsPercentage;

  // Se usuário não tem pontos, mostrar apenas o valor total
  if (!hasPoints) {
    return (
      <Container>
        <SliderLabels>
          <span className="label-money">Valor a pagar</span>
        </SliderLabels>

        <SliderTrack>
          <SliderSegment className="segment-money" style={{ width: "100%" }} />
        </SliderTrack>

        <SliderValues>
          <ValueItem className="value-money">
            {FormatCurrency(roundCurrency(calculations.safeTotal))}
          </ValueItem>
        </SliderValues>
      </Container>
    );
  }

  return (
    <Container>
      <SliderLabels>
        <span className="label-points">Pontos</span>
        <span className="label-money">Valor</span>
      </SliderLabels>

      <SliderTrack>
        <SliderSegment
          className="segment-points"
          style={{ width: `${currentValues.pointsPercentage}%` }}
        />

        {showMoneySegment && (
          <SliderSegment
            className="segment-money"
            style={{ width: `${currentValues.moneyPercentage}%` }}
          />
        )}

        <SliderThumb
          className={showMoneySegment ? "thumb-money" : "thumb-points"}
          style={{ left: `${thumbPosition}%` }}
        />

        <Slider
          value={100 - sliderValue}
          onChange={handleChange}
          onChangeCommitted={handleChangeCommitted}
          disabled={disabled}
          min={0}
          max={100}
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            opacity: 0,
            cursor: disabled ? "not-allowed" : "pointer",
            "& .MuiSlider-thumb": { display: "none" },
            "& .MuiSlider-track": { display: "none" },
            "& .MuiSlider-rail": { display: "none" },
          }}
        />
      </SliderTrack>

      <SliderValues>
        <ValueItem className="value-points">
          {formatPoints(currentValues.pointsToUse)} pts
        </ValueItem>

        {showMoneySegment && (
          <>
            <span className="separator">+</span>
            <ValueItem className="value-money">
              {FormatCurrency(roundCurrency(currentValues.moneyToPay))}
            </ValueItem>
          </>
        )}
      </SliderValues>
    </Container>
  );
}

SliderCheckout.propTypes = {
  totalPoints: PropTypes.number,
  totalMoney: PropTypes.number,
  userBalance: PropTypes.number,
  minPoints: PropTypes.number,
  onChange: PropTypes.func,
  disabled: PropTypes.bool,
  factor: PropTypes.number,
};

export { SliderCheckout };
